/**
 * ownProfileService — PPL-395.
 *
 * The two callables the web had never wrapped, needed by the own-profile page
 * (/profile). Same shape as badgeService.ts: module-level httpsCallable refs
 * plus typed async wrappers. Nothing here touches src/firebaseConfig.js.
 *
 * Response shapes verified against cloud-functions/functions/index.js
 * (getUserStatsOptimizedEurW at 12051-12486, getProfileViewsEurW at 12613+).
 */

import { httpsCallable } from 'firebase/functions';
import { FIREBASE_FUNCTIONS } from '../firebaseConfig';
import type { TimestampLike } from '../types/pplTalk';

const getUserStatsOptimizedEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getUserStatsOptimizedEurW');
const getProfileViewsEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getProfileViewsEurW');

// ─── Types ──────────────────────────────────────────────────────────────────

export interface OwnStats {
  totalPosts: number;
  totalReplies: number;
  totalLikesGiven: number;
  totalLikesReceived: number;
  totalPostLikesReceived: number;
  totalReplyLikesReceived: number;
}

export interface OwnPost {
  id: string;
  title?: string;
  body?: string;
  timestamp: TimestampLike;
  likes: number;
  likeCount: number;
  likedBy: string[];
  /** reply COUNT, not a list (server sends `replyCount`) */
  replies: number;
  categories: string[];
  userId: string;
}

export interface OwnReply {
  id: string;
  text: string;
  timestamp: TimestampLike;
  likes: number;
  likeCount: number;
  likedBy: string[];
  userId: string;
  originalMessageId: string | null;
  originalMessageTitle: string | null;
  originalMessageCategories: string[] | null;
  originalMessageBody: string | null;
  replyId: string;
  type: 'reply';
}

/** The reply cursor the server hands back — passed straight back in verbatim. */
export interface ReplyCursor {
  id: string;
  timestamp?: TimestampLike;
}

export interface UserStatsPage {
  /**
   * true when the call did not come back with real data. Every failure still
   * resolves (the page must never blank), so this is the ONLY way a caller can
   * tell "no activity" apart from "the activity did not load" — a paginator
   * that ignores it would truncate the list on one transient error.
   */
  failed: boolean;
  stats: OwnStats;
  posts: OwnPost[];
  replies: OwnReply[];
  hasMorePosts: boolean;
  hasMoreReplies: boolean;
  /** a ppltalk_messages doc id */
  lastPostDoc: string | null;
  lastReplyDoc: ReplyCursor | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

const strArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

/**
 * getUserStatsOptimizedEurW hands back its Firestore Timestamps unserialised,
 * so over the callable wire they arrive as `{_seconds, _nanoseconds}` — which
 * toDateSafe() does not recognise, and every date would render blank.
 * (getPplTalkMessagesEurW re-serialises to `{seconds, nanoseconds}` itself,
 * which is why the public profile's dates work without this.)
 */
const toTimestamp = (v: unknown): TimestampLike => {
  if (v && typeof v === 'object') {
    const o = v as { _seconds?: unknown; _nanoseconds?: unknown; seconds?: unknown };
    if (typeof o.seconds !== 'number' && typeof o._seconds === 'number') {
      return {
        seconds: o._seconds,
        nanoseconds: typeof o._nanoseconds === 'number' ? o._nanoseconds : 0,
      };
    }
  }
  return (v ?? null) as TimestampLike;
};

function emptyStats(): OwnStats {
  return {
    totalPosts: 0,
    totalReplies: 0,
    totalLikesGiven: 0,
    totalLikesReceived: 0,
    totalPostLikesReceived: 0,
    totalReplyLikesReceived: 0,
  };
}

/** A zeroed page — what every failure degrades to, so the page never blanks. */
export function emptyStatsPage(failed = false): UserStatsPage {
  return {
    failed,
    stats: emptyStats(),
    posts: [],
    replies: [],
    hasMorePosts: false,
    hasMoreReplies: false,
    lastPostDoc: null,
    lastReplyDoc: null,
  };
}

function toPost(raw: Record<string, unknown>): OwnPost {
  return {
    id: String(raw.id ?? ''),
    title: typeof raw.title === 'string' ? raw.title : undefined,
    body: typeof raw.body === 'string' ? raw.body : undefined,
    timestamp: toTimestamp(raw.timestamp),
    likes: num(raw.likes),
    likeCount: num(raw.likeCount),
    likedBy: strArray(raw.likedBy),
    replies: num(raw.replies),
    categories: strArray(raw.categories),
    userId: String(raw.userId ?? ''),
  };
}

function toReply(raw: Record<string, unknown>): OwnReply {
  return {
    id: String(raw.id ?? ''),
    text: typeof raw.text === 'string' ? raw.text : '',
    timestamp: toTimestamp(raw.timestamp),
    likes: num(raw.likes),
    likeCount: num(raw.likeCount),
    likedBy: strArray(raw.likedBy),
    userId: String(raw.userId ?? ''),
    originalMessageId: typeof raw.originalMessageId === 'string' ? raw.originalMessageId : null,
    originalMessageTitle: typeof raw.originalMessageTitle === 'string' ? raw.originalMessageTitle : null,
    originalMessageCategories: Array.isArray(raw.originalMessageCategories)
      ? strArray(raw.originalMessageCategories)
      : null,
    originalMessageBody: typeof raw.originalMessageBody === 'string' ? raw.originalMessageBody : null,
    replyId: String(raw.replyId ?? raw.id ?? ''),
    type: 'reply',
  };
}

// ─── Calls ──────────────────────────────────────────────────────────────────

/**
 * One round-trip returns the header stats AND the first page of both posts and
 * replies (`includeContent: true`).
 *
 * The server REQUIRES `userId` in request.data even for self — it throws
 * invalid-argument without it and does not read request.auth at all.
 *
 * Offsets: `postsOffset` takes the string `lastPostDoc`; `repliesOffset` takes
 * the `lastReplyDoc` OBJECT verbatim — the server normalises `{id: ...}` itself
 * (index.js 12058-12059). Never wrap lastReplyDoc a second time (the app does,
 * and its replies pagination is broken because of it).
 *
 * Never throws: a failure resolves to a zeroed page carrying `failed: true`,
 * so the page can degrade to zeros AND still say the load failed rather than
 * presenting an empty account as fact.
 */
export async function getUserStats(params: {
  userId: string;
  includeContent?: boolean;
  limit?: number;
  postsOffset?: string | null;
  repliesOffset?: ReplyCursor | string | null;
}): Promise<UserStatsPage> {
  const { userId, includeContent = true, limit = 10, postsOffset = null, repliesOffset = null } = params;
  if (!userId) return emptyStatsPage(true);

  try {
    const res = await getUserStatsOptimizedEurW({
      userId,
      includeContent,
      limit,
      postsOffset,
      repliesOffset,
      excludeBots: false,
    });
    const envelope = (res.data ?? {}) as { success?: boolean; data?: Record<string, unknown> };
    if (envelope.success !== true || !envelope.data) return emptyStatsPage(true);

    const d = envelope.data;
    const rawStats = (d.stats ?? {}) as Record<string, unknown>;
    const rawPosts = Array.isArray(d.posts) ? (d.posts as Record<string, unknown>[]) : [];
    const rawReplies = Array.isArray(d.replies) ? (d.replies as Record<string, unknown>[]) : [];
    const rawReplyCursor = d.lastReplyDoc as Record<string, unknown> | null | undefined;

    return {
      failed: false,
      stats: {
        totalPosts: num(rawStats.totalPosts),
        totalReplies: num(rawStats.totalReplies),
        totalLikesGiven: num(rawStats.totalLikesGiven),
        totalLikesReceived: num(rawStats.totalLikesReceived),
        totalPostLikesReceived: num(rawStats.totalPostLikesReceived),
        totalReplyLikesReceived: num(rawStats.totalReplyLikesReceived),
      },
      posts: rawPosts.map(toPost),
      replies: rawReplies.map(toReply),
      hasMorePosts: d.hasMorePosts === true,
      hasMoreReplies: d.hasMoreReplies === true,
      lastPostDoc: typeof d.lastPostDoc === 'string' ? d.lastPostDoc : null,
      lastReplyDoc:
        rawReplyCursor && typeof rawReplyCursor.id === 'string'
          ? { id: rawReplyCursor.id, timestamp: (rawReplyCursor.timestamp ?? null) as TimestampLike }
          : null,
    };
  } catch {
    return emptyStatsPage(true);
  }
}

/**
 * Profile view COUNT. `countOnly: true` short-circuits before the premium gate
 * (index.js 12626), so the number is free-tier safe — only the viewer RECORDS
 * are premium-gated. The uid comes from request.auth; never pass one.
 *
 * Returns 0 on any error (non-critical, mirrors the app).
 */
export async function getProfileViewCount(): Promise<number> {
  try {
    const res = await getProfileViewsEurW({ countOnly: true });
    const data = (res.data ?? {}) as { success?: boolean; count?: unknown };
    if (data.success !== true) return 0;
    return num(data.count);
  } catch {
    return 0;
  }
}
